import * as React from "react"
import {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { ArrowUpDown, ChevronDown, MoreHorizontal, ArrowDown, ArrowUp, GripVertical } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useTranslation } from "react-i18next"

type DataTableProps<T> = {
  data: T[]
  columns: ColumnDef<T>[]
  filterColumn: string
  filterName: string
  renderButton?: () => React.ReactNode
  enableManualOrder?: boolean
  onDataChange?: (nextData: T[]) => void
}

export function DataTable<T>({
  data,
  columns,
  filterColumn,
  filterName,
  renderButton,
  enableManualOrder = false,
  onDataChange,
}: DataTableProps<T>) {
  const {t, i18n} = useTranslation();
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  )
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = React.useState({})
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const draggingRowIndexRef = React.useRef<number | null>(null)

  const moveRow = React.useCallback((fromIndex: number, toIndex: number) => {
    if (!onDataChange) {
      return
    }
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
      return
    }

    const nextData = [...data]
    const [moved] = nextData.splice(fromIndex, 1)
    nextData.splice(toIndex, 0, moved)
    onDataChange(nextData)
  }, [data, onDataChange])

  const moveRowByOffset = React.useCallback((fromIndex: number, offset: number) => {
    moveRow(fromIndex, fromIndex + offset)
  }, [moveRow])

  const manualColumns: ColumnDef<T>[] = enableManualOrder
    ? [
        {
          id: "manualOrder",
          header: () => <span>{t("common.dragsort")}</span>,
          cell: ({ row }) => {
            const rowIndex = row.index
            const canMoveUp = rowIndex > 0
            const canMoveDown = rowIndex < data.length - 1

            return (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 cursor-grab active:cursor-grabbing"
                  draggable
                  onDragStart={(event) => {
                    draggingRowIndexRef.current = rowIndex
                    event.dataTransfer.setData("text/plain", String(rowIndex))
                    event.dataTransfer.effectAllowed = "move"
                  }}
                  onDragEnd={() => {
                    draggingRowIndexRef.current = null
                  }}
                  aria-label={t("common.dragsort")}
                >
                  <GripVertical className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  disabled={!canMoveUp}
                  onClick={() => moveRowByOffset(rowIndex, -1)}
                  aria-label={t("common.moveupone")}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  disabled={!canMoveDown}
                  onClick={() => moveRowByOffset(rowIndex, 1)}
                  aria-label={t("common.movedownone")}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
              </div>
            )
          },
          enableSorting: false,
          enableHiding: false,
        },
      ]
    : []

  const displayedColumns = [...manualColumns, ...columns]

  const table = useReactTable({
    data,
    columns: displayedColumns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: enableManualOrder ? undefined : getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
  })

  React.useEffect(() => {
    const pageCount = Math.max(table.getPageCount(), 1)
    setPagination((prev) => {
      const nextPageIndex = Math.min(prev.pageIndex, pageCount - 1)
      if (nextPageIndex === prev.pageIndex) {
        return prev
      }
      return {
        ...prev,
        pageIndex: nextPageIndex,
      }
    })
  }, [table, data.length, columnFilters, sorting])

  return (
    <div className="w-full bg-muted px-4 rounded-2xl">
      <div className="flex items-center py-4 gap-4">
        <Input
          placeholder={t("component.filter") + filterName}
          value={(table.getColumn(filterColumn)?.getFilterValue() as string) ?? ""}
          onChange={(event) =>
            table.getColumn(filterColumn)?.setFilterValue(event.target.value)
          }
          className="max-w-sm"
        />
        { renderButton && renderButton() }
        {/* <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="ml-auto">
              Columns <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {column.id}
                  </DropdownMenuCheckboxItem>
                )
              })}
          </DropdownMenuContent>
        </DropdownMenu> */}
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  onDragOver={enableManualOrder ? (event) => {
                    event.preventDefault()
                  } : undefined}
                  onDrop={enableManualOrder ? (event) => {
                    event.preventDefault()
                    const fromIndex = draggingRowIndexRef.current ?? Number.parseInt(event.dataTransfer.getData("text/plain"), 10)
                    if (!Number.isNaN(fromIndex)) {
                      moveRow(fromIndex, row.index)
                    }
                    draggingRowIndexRef.current = null
                  } : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 py-4">
        {/* <div className="flex-1 text-sm text-muted-foreground">
          {table.getFilteredSelectedRowModel().rows.length} of{" "}
          {table.getFilteredRowModel().rows.length} row(s) selected.
        </div> */}
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {t("component.prev")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {t("component.next")}
          </Button>
        </div>
      </div>
    </div>
  )
}
